import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DollarSign,
  Calendar,
  TrendingUp,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Get API base URL from environment variable or use default
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || '/api/v1';

interface Payout {
  id: string;
  total_commission: number;
  commission_count: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  completed_at?: string;
  stripe_payout_id?: string;
  failure_reason?: string;
}

interface EarningsData {
  total_referrals: number;
  total_revenue_generated: number;
  total_commission_earned: number;
  pending_commission: number;
  approved_commission: number;
  paid_commission: number;
  total_payouts: number;
  last_payout_date?: string;
  recent_payouts: Payout[];
}

const statusConfig = {
  pending: {
    label: 'Pending',
    icon: Clock,
    variant: 'secondary' as const,
    color: 'text-yellow-600'
  },
  processing: {
    label: 'Processing',
    icon: Loader2,
    variant: 'secondary' as const,
    color: 'text-blue-600'
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    variant: 'default' as const,
    color: 'text-green-600'
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    variant: 'destructive' as const,
    color: 'text-red-600'
  },
  cancelled: {
    label: 'Cancelled',
    icon: AlertCircle,
    variant: 'outline' as const,
    color: 'text-gray-600'
  }
};

export const PayoutHistory = () => {
  const { data: earnings, isLoading, error } = useQuery<EarningsData>({
    queryKey: ['practitioner-earnings'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/referrals/practitioner/earnings`, {
        credentials: 'include', // Send cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to fetch earnings data');
      }

      return response.json();
    },
    staleTime: 60000, // 1 minute
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
          <CardDescription>Loading your payout history...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load payout history. Please try again later.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const payouts = earnings?.recent_payouts || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary" />
          Payout History
        </CardTitle>
        <CardDescription>
          Track your referral commission payouts and earnings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-900 dark:text-green-100">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-green-700 dark:text-green-300">
              ${(earnings?.paid_commission || 0).toFixed(2)}
            </p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {earnings?.total_payouts || 0} payout{(earnings?.total_payouts || 0) !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">Pending</span>
            </div>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
              ${((earnings?.pending_commission || 0) + (earnings?.approved_commission || 0)).toFixed(2)}
            </p>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              Available for payout
            </p>
          </div>

          <div className="p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950 dark:to-violet-950 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-900 dark:text-purple-100">Last Payout</span>
            </div>
            <p className="text-lg font-semibold text-purple-700 dark:text-purple-300">
              {earnings?.last_payout_date
                ? formatDistanceToNow(new Date(earnings.last_payout_date), { addSuffix: true })
                : 'No payouts yet'
              }
            </p>
          </div>
        </div>

        {/* Payout Table */}
        {payouts.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No Payouts Yet</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Once you've accumulated referral commissions and meet the minimum payout threshold,
              your payouts will appear here.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Commissions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Transfer ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((payout) => {
                  const config = statusConfig[payout.status];
                  const StatusIcon = config.icon;

                  return (
                    <TableRow key={payout.id}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span>{new Date(payout.created_at).toLocaleDateString()}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(payout.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          ${payout.total_commission.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {payout.commission_count} item{payout.commission_count !== 1 ? 's' : ''}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={config.variant} className="gap-1">
                          <StatusIcon className={`h-3 w-3 ${payout.status === 'processing' ? 'animate-spin' : ''}`} />
                          {config.label}
                        </Badge>
                        {payout.failure_reason && (
                          <div className="text-xs text-red-600 dark:text-red-400 mt-1 max-w-xs">
                            {payout.failure_reason}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {payout.stripe_payout_id ? (
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {payout.stripe_payout_id.slice(0, 20)}...
                          </code>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Info Notice */}
        <div className="p-4 bg-muted/50 rounded-lg text-sm space-y-2">
          <h4 className="font-semibold">About Payouts</h4>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Payouts are processed automatically when your balance reaches $20.00</li>
            <li>• Funds are transferred to your Stripe Connect account</li>
            <li>• Bank deposits typically take 2-7 business days</li>
            <li>• You'll receive an email notification when each payout is processed</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
